import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@simplewebauthn/browser";
import { api } from "./api";

type RegistrationOptions = { challengeId: string; options: PublicKeyCredentialCreationOptionsJSON };
type AuthenticationOptions = { challengeId: string; options: PublicKeyCredentialRequestOptionsJSON };

export type PasskeyStatus = { supported: boolean; registered: boolean; credentialCount: number };

type MobilePlatformInput = {
  userAgent?: string;
  mobileHint?: boolean;
  maxTouchPoints?: number;
};

const CREDENTIAL_MANAGER_SETTLE_MS = 350;
const CREDENTIAL_MANAGER_RETRY_MS = 450;
let lastVisibleAt = Date.now();
let authenticationInFlight: Promise<{ userId: string; expiresAt: string; mfaVerified: true }> | null = null;

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") lastVisibleAt = Date.now();
  });
}

function delay(milliseconds: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, milliseconds));
}

async function waitForCredentialManager() {
  if (typeof document === "undefined") return;

  if (document.readyState !== "complete") {
    await new Promise<void>((resolve) => window.addEventListener("load", () => resolve(), { once: true }));
  }

  if (document.visibilityState !== "visible") {
    await new Promise<void>((resolve) => {
      const onVisibilityChange = () => {
        if (document.visibilityState !== "visible") return;
        document.removeEventListener("visibilitychange", onVisibilityChange);
        resolve();
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
    });
  }

  const remainingSettleTime = CREDENTIAL_MANAGER_SETTLE_MS - (Date.now() - lastVisibleAt);
  if (remainingSettleTime > 0) await delay(remainingSettleTime);
  await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
}

export function isMobilePlatform(input: MobilePlatformInput = {}) {
  const userAgent = input.userAgent ?? navigator.userAgent;
  const mobileHint = input.mobileHint
    ?? (navigator as Navigator & { userAgentData?: { mobile?: boolean } }).userAgentData?.mobile;
  const maxTouchPoints = input.maxTouchPoints ?? navigator.maxTouchPoints;
  return mobileHint === true
    || /Android|iPhone|iPad|iPod/i.test(userAgent)
    || (/Macintosh/i.test(userAgent) && maxTouchPoints > 1);
}

export async function passkeysAvailable() {
  if (!browserSupportsWebAuthn()) return false;
  try { return await platformAuthenticatorIsAvailable(); }
  catch { return false; }
}

export async function registerPasskey(label = "Biometria do aparelho") {
  await waitForCredentialManager();
  const request = await api<RegistrationOptions>("/auth/passkeys/registration/options", { method: "POST" });
  const response = await startRegistration({ optionsJSON: request.options });
  return api<{ registered: true }>("/auth/passkeys/registration/verify", {
    method: "POST",
    body: JSON.stringify({ challengeId: request.challengeId, response, label }),
  });
}

export function isTransientCredentialManagerError(error: unknown) {
  const name = error instanceof Error ? error.name : "";
  const message = error instanceof Error ? error.message : "";
  return name === "UnknownError"
    || /unknown error.*credential manager|talking to the credential manager|credential manager.*(?:busy|unavailable)/i.test(message);
}

async function performPasskeyAuthentication() {
  await waitForCredentialManager();
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const request = await api<AuthenticationOptions>("/auth/passkeys/authentication/options", { method: "POST" });
      const response = await startAuthentication({ optionsJSON: request.options });
      return await api<{ userId: string; expiresAt: string; mfaVerified: true }>("/auth/passkeys/authentication/verify", {
        method: "POST",
        body: JSON.stringify({ challengeId: request.challengeId, response }),
      });
    } catch (error) {
      if (attempt > 0 || !isTransientCredentialManagerError(error)) throw error;
      await delay(CREDENTIAL_MANAGER_RETRY_MS);
    }
  }
  throw new Error("Não foi possível iniciar a biometria.");
}

export function authenticateWithPasskey() {
  if (authenticationInFlight) return authenticationInFlight;
  authenticationInFlight = performPasskeyAuthentication().finally(() => {
    authenticationInFlight = null;
  });
  return authenticationInFlight;
}

export function passkeyErrorMessage(error: unknown, fallback: string) {
  const name = error instanceof Error ? error.name : "";
  const message = error instanceof Error ? error.message : "";
  if (
    name === "NotAllowedError"
    || name === "AbortError"
    || /not.?allowed|timed?.?out|timeout|cancelled|canceled/i.test(message)
  ) {
    return "Biometria não confirmada. Tente novamente ou use sua senha.";
  }
  if (name === "SecurityError" || /invalid domain|webauthn.*unavailable|www\.w3\.org/i.test(message)) {
    return "A biometria não está disponível neste acesso. Use sua senha.";
  }
  if (isTransientCredentialManagerError(error)) {
    return "A biometria ainda não estava pronta. Aguarde um instante e tente novamente.";
  }
  return message || fallback;
}
